import type { AxiosInstance } from 'axios';

import type { RfqStatus } from '@accio3d/shared-ui/lib/rfqStatus';
import type { FunctionalRequirements } from '@accio3d/shared-ui/printability/services/printability3dService';
import { fetchPaginatedList } from '@accio3d/shared-ui/services/pagination';
import type { PagePaginatedResponse, PaginationParams } from '@accio3d/shared-ui/types/pagination';
import type { ShippingRateAddress } from './quotes';

/**
 * One part's spec on an RFQ. This subset is what the purchaser RFQ payload
 * inlines; the supplier payload and the `/requirements` routes return the
 * fuller `RfqRequirement`.
 */
export interface RfqRequirementSummary {
  id: string;
  partId: string;
  partName: string | null;
  partNumber: string | null;
  /** Signed STL mesh from the latest COMPLETE 3D analysis; null without one. */
  previewUrl: string | null;
  /** Static list thumbnail, resolved wherever previewUrl is. */
  thumbnailUrl: string | null;
  quantity: number;
  material: string | null;
  processType: string | null;
  postProcesses: string[];
  color: string | null;
  infill: number | null;
  partLengthMm: number | null;
  partWidthMm: number | null;
  partHeightMm: number | null;
  partVolumeMm3: number | null;
  partMassG: number | null;
  partSpecsConfirmedAt: string | null;
  functionalRequirements: FunctionalRequirements | null;
  currentPrice: number | null;
  targetPrice: number | null;
}

export interface RfqRequirement extends RfqRequirementSummary {
  rfqId: string;
  tightestToleranceMm: number | null;
  notes: string | null;
  documentFileUrls: string[];
  /** Signed STL mesh from the latest COMPLETE 3D analysis; null without one. */
  previewUrl: string | null;
  /** Static list thumbnail, resolved wherever previewUrl is. */
  thumbnailUrl: string | null;
  priceSpreadsheet: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierRfq {
  id: string;
  name: string | null;
  status: RfqStatus;
  /** True when this RFQ's REJECTED status came from the supplier's own Deny action. */
  deniedBySupplier: boolean;
  rfqNumber: string;
  /** The parent RFQ id — shared by every supplier this RFQ was sent to. */
  rfqId: string;
  shippingAddress: string | null;
  supplierLocation: string | null;
  isReconstruction: boolean;
  supplierId: string | null;
  /** Null on list responses — only the detail endpoint joins these. */
  organizationName: string | null;
  purchaserName: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  manufacturingRequirements: RfqRequirement[];
  createdAt: string;
  updatedAt: string;
}

export interface QuoteLine {
  id: string;
  manufacturingRequirementId: string;
  partId: string;
  partName: string | null;
  partNumber: string | null;
  /** Signed STL mesh from the latest COMPLETE 3D analysis; null without one. */
  previewUrl: string | null;
  /** Static list thumbnail, resolved wherever previewUrl is. */
  thumbnailUrl: string | null;
  quantity: number;
  pricePerUnit: number | null;
  subTotal: number | null;
  /** Parts only — no shipping or tax, see SupplierQuote.total. */
  total: number | null;
}

/**
 * A quote as the supplier sees it. Mirrors `QuoteResponse` in the API's
 * `quotes/types.ts`; money values are dollar numbers, not cents.
 *
 * Lifecycle: `pending` is the supplier's private draft — the purchaser cannot
 * see it. `send` moves it to `quoted`, which locks editing; `withdraw` is the
 * only way back out of a sent quote.
 */
export interface SupplierQuote {
  id: string;
  rfqId: string;
  organizationId: string;
  supplierId: string;
  purchaserId: string | null;
  lines: QuoteLine[];
  subTotal: number | null;
  total: number | null;
  leadTime: number;
  shippingPrice: number | null;
  /** Server-composed address snapshot, NOT the location id that was submitted. */
  shippingFrom: string | null;
  shipFromAddress: ShippingRateAddress | null;
  processType: string | null;
  postProcessing: string[];
  status: 'pending' | 'quoted' | 'accepted' | 'declined' | 'expired' | 'withdrawn';
  quotedOn: string | null;
  acceptedOn: string | null;
  isLocked: boolean;
  notes: string | null;
  /**
   * The parcel the supplier will ship, in `packageUnits` (lengths) and the
   * weight unit that pairs with it — grams for metric lengths, ounces for
   * imperial. The API feeds these straight to EasyPost as the box; they are
   * not any one part's geometry.
   */
  packageLength: number | null;
  packageWidth: number | null;
  packageHeight: number | null;
  packageWeight: number | null;
  packageUnits: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * One part's price on the quote. The API seeds a line per manufacturing
 * requirement on the RFQ when the draft is created, so these are overrides;
 * `quantity` defaults to the requirement's own.
 */
export interface QuoteLineInput {
  manufacturingRequirementId: string;
  quantity?: number;
  pricePerUnit?: number | null;
}

/**
 * Draft payload. `shippingFrom` is a storefront LOCATION ID — the API resolves it
 * against the supplier's own locations and stores its own address snapshot, so
 * sending free-text here does nothing.
 *
 * `subTotal`/`total` are never client-settable; the API sums the lines and adds
 * shippingPrice.
 */
export interface CreateQuoteInput {
  rfqId: string;
  lines?: QuoteLineInput[];
  leadTime?: number;
  shippingPrice?: number;
  shippingFrom?: string;
  processType?: string;
  postProcessing?: string[];
  notes?: string;
  /** Parcel dimensions — required before the quote can be sent. */
  packageLength?: number;
  packageWidth?: number;
  packageHeight?: number;
  packageWeight?: number;
  packageUnits?: string;
}

export type UpdateQuoteInput = Omit<CreateQuoteInput, 'rfqId'>;

/**
 * A supplier's question about an RFQ and the purchaser's answer.
 *
 * Both parties read the same rows (dual-party RLS), but only the supplier asks
 * or withdraws and only the purchaser answers.
 */
export interface RfqInfoRequest {
  id: string;
  rfqId: string;
  supplierId: string;
  supplierName: string | null;
  question: string;
  status: 'open' | 'answered' | 'withdrawn';
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One message in the purchaser<->supplier correspondence thread on an RFQ. */
export interface RfqMessage {
  id: string;
  rfqId: string;
  senderOrgId: string;
  senderRole: 'purchaser' | 'supplier';
  senderUserId: string | null;
  senderName: string | null;
  senderOrgName: string | null;
  body: string;
  createdAt: string;
}

/** A page of messages, oldest first within the page. */
export interface RfqMessagesPage {
  data: RfqMessage[];
  nextCursor: string | null;
  hasMore: boolean;
}

export function createRfqsService(client: AxiosInstance) {
  return {
    async list(
      pagination?: PaginationParams & { status?: RfqStatus; search?: string },
    ): Promise<PagePaginatedResponse<SupplierRfq>> {
      const params: Record<string, string> = {};
      if (pagination?.status) params.status = pagination.status;
      if (pagination?.search) params.search = pagination.search;
      return fetchPaginatedList<SupplierRfq>(client, '/suppliers/rfqs', pagination, {
        params,
      });
    },

    async get(id: string): Promise<SupplierRfq> {
      const { data } = await client.get<SupplierRfq>(`/suppliers/rfqs/${encodeURIComponent(id)}`);
      return data;
    },

    async listRequirements(rfqId: string): Promise<RfqRequirement[]> {
      const { data } = await client.get<RfqRequirement[]>(
        `/suppliers/rfqs/${encodeURIComponent(rfqId)}/requirements`,
      );
      return data;
    },

    /** The supplier's own quotes, optionally scoped to one RFQ. */
    async listQuotes(
      params?: PaginationParams & { rfqId?: string; status?: string },
    ): Promise<PagePaginatedResponse<SupplierQuote>> {
      return fetchPaginatedList<SupplierQuote>(client, '/suppliers/quotes', params, {
        params: { rfqId: params?.rfqId, status: params?.status },
      });
    },

    async createQuote(body: CreateQuoteInput): Promise<SupplierQuote> {
      const { data } = await client.post<SupplierQuote>('/suppliers/quotes', body);
      return data;
    },

    async updateQuote(id: string, body: UpdateQuoteInput): Promise<SupplierQuote> {
      const { data } = await client.patch<SupplierQuote>(
        `/suppliers/quotes/${encodeURIComponent(id)}`,
        body,
      );
      return data;
    },

    /** Publishes the draft to the purchaser. Rejected unless priced and ship-from set. */
    async sendQuote(id: string): Promise<SupplierQuote> {
      const { data } = await client.post<SupplierQuote>(
        `/suppliers/quotes/${encodeURIComponent(id)}/send`,
      );
      return data;
    },

    async withdrawQuote(id: string): Promise<SupplierQuote> {
      const { data } = await client.post<SupplierQuote>(
        `/suppliers/quotes/${encodeURIComponent(id)}/withdraw`,
      );
      return data;
    },

    async listInfoRequests(rfqId: string): Promise<RfqInfoRequest[]> {
      const { data } = await client.get<RfqInfoRequest[]>(
        `/suppliers/rfqs/${encodeURIComponent(rfqId)}/info-requests`,
      );
      return data;
    },

    /** Asks the purchaser for a detail the RFQ doesn't carry. */
    async askInfoRequest(rfqId: string, question: string): Promise<RfqInfoRequest> {
      const { data } = await client.post<RfqInfoRequest>(
        `/suppliers/rfqs/${encodeURIComponent(rfqId)}/info-requests`,
        { question },
      );
      return data;
    },

    /** Declines a matched or quoted RFQ. Reason is optional and internal-only. */
    async denyRfq(id: string, reason?: string): Promise<SupplierRfq> {
      const { data } = await client.post<SupplierRfq>(
        `/suppliers/rfqs/${encodeURIComponent(id)}/deny`,
        { reason },
      );
      return data;
    },

    async withdrawInfoRequest(requestId: string): Promise<RfqInfoRequest> {
      const { data } = await client.post<RfqInfoRequest>(
        `/suppliers/rfqs/info-requests/${encodeURIComponent(requestId)}/withdraw`,
      );
      return data;
    },

    async listMessages(rfqId: string, params?: { cursor?: string }): Promise<RfqMessagesPage> {
      const { data } = await client.get<RfqMessagesPage>(
        `/suppliers/rfqs/${encodeURIComponent(rfqId)}/messages`,
        { params },
      );
      return data;
    },

    async sendMessage(rfqId: string, body: string): Promise<RfqMessage> {
      const { data } = await client.post<RfqMessage>(
        `/suppliers/rfqs/${encodeURIComponent(rfqId)}/messages`,
        { body },
      );
      return data;
    },
  };
}

export type RfqsService = ReturnType<typeof createRfqsService>;
